import clsx from 'clsx';

import * as api from '@gitbook/api-client';
import { Icon, type SVGIconComponent } from '@gitbook/spine-icons';
import { renderIcon } from '@gitbook/spine-react';

export const DEFAULT_CHANGE_REQUEST_STATUS = api.ChangeRequestStatus.Open;

type ChangeRequestStatusDescriptor = {
    label: string;
    description: string;
    icon: SVGIconComponent;
    textColorClassName: string;
    kind: 'secondary' | 'success' | 'merge' | 'danger';
};

const CHANGE_REQUEST_STATUSES_MAP: Record<api.ChangeRequestStatus, ChangeRequestStatusDescriptor> =
    {
        [api.ChangeRequestStatus.Draft]: {
            label: 'Draft',
            description: 'This change request is currently a draft',
            icon: Icon.ChangeRequestDraft,
            textColorClassName: 'text-secondary',
            kind: 'secondary',
        },
        [api.ChangeRequestStatus.Open]: {
            label: 'In review',
            description: 'This change request has been submitted for review',
            icon: Icon.ChangeRequestOpen,
            textColorClassName: 'text-success',
            kind: 'success',
        },
        [api.ChangeRequestStatus.Merged]: {
            label: 'Merged',
            description: 'This change request has been merged into the space',
            icon: Icon.ChangeRequestMerged,
            textColorClassName: 'text-merge',
            kind: 'merge',
        },
        [api.ChangeRequestStatus.Archived]: {
            label: 'Archived',
            description: 'This change request has been archived',
            icon: Icon.ChangeRequestArchive,
            textColorClassName: 'text-danger',
            kind: 'danger',
        },
    };

export const CHANGE_REQUEST_STATUSES = Object.entries(CHANGE_REQUEST_STATUSES_MAP).map(
    ([status, entry]) => ({
        ...entry,
        value: status as api.ChangeRequestStatus,
    })
);

/**
 * Returns the descriptor of a change request status.
 */
export function getChangeChangeRequestStatusDescriptor(
    status: api.ChangeRequestStatus
): ChangeRequestStatusDescriptor {
    return CHANGE_REQUEST_STATUSES_MAP[status];
}

/**
 * Returns the description of a change request status.
 */
export function getChangeRequestStatusDescription(status: api.ChangeRequestStatus): string {
    return getChangeChangeRequestStatusDescriptor(status).description;
}

/**
 * Returns the label of a change request status.
 */
export function getChangeRequestStatusLabel(status: api.ChangeRequestStatus) {
    return getChangeChangeRequestStatusDescriptor(status).label;
}

/**
 * Icon of a change request status.
 */
export function ChangeRequestStatusIcon(props: {
    status: api.ChangeRequestStatus;
    className?: string;
    size?: number;
    /**
     * Color the icon.
     * @default false
     */
    colored?: boolean;
}) {
    const { status, className, size, colored } = props;
    const descriptor = getChangeChangeRequestStatusDescriptor(status);
    return renderIcon(descriptor.icon, {
        size,
        className: clsx(colored ? descriptor.textColorClassName : null, className),
    });
}
